import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import api from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';

const EVIDENCE_STATE_PLAIN = {
  supported:   'Supported',
  conflicting: 'Conflicting',
  uncertain:   'Uncertain',
  missing:     'Missing',
};

const EVIDENCE_DOT = {
  supported:   '#03f37c',
  conflicting: '#000000',
  uncertain:   '#737373',
  missing:     '#d4d4d4',
};

const FINDING_TYPE_STYLE = {
  'Changed':        'bg-gray-100 text-gray-700 border border-gray-200',
  'Conflict':       'bg-black text-white border border-black',
  'Stale':          'bg-gray-100 text-gray-600 border border-gray-200',
  'Missing source': 'bg-gray-50 text-gray-500 border border-gray-200',
};

const REVIEW_STATE_COLOR = {
  Pending:   '#a3a3a3',
  Approved:  '#03f37c',
  Rejected:  '#737373',
  Held:      '#737373',
  Escalated: '#000000',
};

const ACTIONS = [
  { value: 'approve',  label: 'Approve recommendation' },
  { value: 'reject',   label: 'Reject recommendation' },
  { value: 'hold',     label: 'Hold' },
  { value: 'escalate', label: 'Escalate' },
];

const NOTE_REQUIRED = ['reject', 'hold', 'escalate'];

export default function FindingDetail() {
  const { id } = useParams();
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const [showReview, setShowReview] = useState(false);
  const [action, setAction]         = useState('');
  const [note, setNote]             = useState('');
  const [formError, setFormError]   = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult]         = useState(null);

  useEffect(() => {
    api.get(`/brain/findings/${id}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [id]);

  const openReview = () => {
    setAction('');
    setNote('');
    setFormError('');
    setResult(null);
    setShowReview(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!action) {
      setFormError('Please select an action.');
      return;
    }
    if (NOTE_REQUIRED.includes(action) && !note.trim()) {
      setFormError('A note is required for this action.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/review', { finding_id: id, action, note });
      setResult(res.data);
      setData(prev => prev ? {
        ...prev,
        finding: { ...prev.finding, review_state: res.data.resulting_state }
      } : prev);
    } catch (e) {
      setFormError(e.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p className="font-body text-gray-400 py-8">Loading finding…</p>;
  if (error)   return <p className="font-body text-gray-700 py-8">Error: {error}</p>;
  if (!data)   return <p className="font-body text-gray-400 py-8">Finding not found.</p>;

  const { finding, evidence } = data;
  const isPending = finding.review_state === 'Pending';

  return (
    <div data-testid="finding-detail-page">
      <Link
        to="/app/brain"
        className="inline-flex items-center gap-1.5 font-body text-sm text-gray-400 hover:text-black mb-6 transition-colors focus-emerald"
        data-testid="back-to-brain"
      >
        <ArrowLeft size={14} />
        Brain
      </Link>

      {/* Finding header */}
      <div className="mb-8">
        <div className="flex flex-wrap items-start gap-3 mb-3">
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-heading font-medium ${FINDING_TYPE_STYLE[finding.finding_type] || 'bg-gray-100 text-gray-600'}`}
            data-testid="finding-type-badge"
          >
            {finding.finding_type}
          </span>
          {finding.abstain && (
            <span
              className="inline-flex items-center px-2.5 py-1 rounded text-xs font-heading font-bold border-2 border-black bg-white text-black"
              data-testid="abstain-notice"
            >
              ABSTAIN — HUMAN REVIEW REQUIRED
            </span>
          )}
          <span
            className="ml-auto font-heading text-sm font-semibold"
            style={{ color: REVIEW_STATE_COLOR[finding.review_state] || '#737373' }}
            data-testid="finding-review-state"
          >
            {finding.review_state}
          </span>
        </div>

        <h1 className="page-heading mb-3" data-testid="finding-title">
          {finding.title}
        </h1>
        <p className="font-body text-gray-600 leading-relaxed max-w-3xl" style={{ fontSize: '15px' }}>
          {finding.summary}
        </p>
      </div>

      {/* Review result banner */}
      {result && (
        <div
          className="mb-6 p-4 border rounded font-body text-sm"
          style={{
            borderColor: result.resulting_state === 'Approved' ? '#03f37c' : '#e5e5e5',
            backgroundColor: result.resulting_state === 'Approved' ? 'rgba(3,243,124,0.05)' : '#fafafa'
          }}
          data-testid="review-result-banner"
        >
          <strong className="font-heading font-semibold text-black">
            Decision recorded: {result.resulting_state}
          </strong>
          {result.canon_note && (
            <p className="mt-1 text-gray-600">{result.canon_note}</p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main detail sections */}
        <div className="lg:col-span-2 space-y-6">

          {/* Abstain notice */}
          {finding.abstain && (
            <div
              className="p-4 border-2 border-black rounded"
              data-testid="abstain-detail"
            >
              <p className="font-heading font-bold text-black text-sm mb-1">
                ABSTAIN — HUMAN REVIEW REQUIRED
              </p>
              <p className="font-body text-gray-600 text-sm leading-relaxed">
                The Brain has identified a direct conflict between two authoritative sources.
                It cannot collapse the conflict into a single answer.
                A named Canon Owner must determine which source takes precedence.
              </p>
            </div>
          )}

          {/* What changed */}
          <section data-testid="section-what-changed">
            <h2 className="section-heading mb-2">What changed</h2>
            <p className="font-body text-gray-700 text-sm leading-relaxed">{finding.what_changed}</p>
          </section>

          {/* Why it matters */}
          <section data-testid="section-why-it-matters">
            <h2 className="section-heading mb-2">Why it matters</h2>
            <p className="font-body text-gray-700 text-sm leading-relaxed">{finding.why_it_matters}</p>
          </section>

          {/* Source */}
          <section data-testid="section-source">
            <h2 className="section-heading mb-2">Source</h2>
            <p className="font-body text-gray-600 text-sm">{finding.source_ref}</p>
            <p className="font-body text-xs text-gray-400 mt-1">Version reference: {finding.version_ref}</p>
          </section>

          {/* Who is affected */}
          <section data-testid="section-who-affected">
            <h2 className="section-heading mb-2">Who is affected</h2>
            <p className="font-body text-gray-700 text-sm leading-relaxed">{finding.who_is_affected}</p>
          </section>

          {/* Recommended next step */}
          <section data-testid="section-next-step">
            <h2 className="section-heading mb-2">Recommended next step</h2>
            <div
              className="p-4 border border-gray-200 rounded bg-gray-50"
            >
              <p className="font-body text-gray-700 text-sm leading-relaxed">{finding.recommended_next_step}</p>
            </div>
          </section>

          {/* Evidence */}
          <section data-testid="section-evidence">
            <h2 className="section-heading mb-4">Evidence records</h2>
            {evidence.length === 0 ? (
              <p className="font-body text-gray-400 text-sm">No evidence records linked to this finding.</p>
            ) : (
              <div className="space-y-4">
                {evidence.map((ev) => (
                  <div
                    key={ev.id}
                    className="border border-gray-200 rounded p-4"
                    data-testid={`evidence-item-${ev.id}`}
                  >
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="font-heading font-semibold text-black text-sm">{ev.source_title}</span>
                      <span className="font-heading text-xs text-gray-400">{ev.source_version}</span>
                      <span className="inline-flex items-center gap-1.5 font-body text-xs text-gray-600 ml-auto">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: EVIDENCE_DOT[ev.evidence_state] || '#a3a3a3' }}
                        />
                        {EVIDENCE_STATE_PLAIN[ev.evidence_state] || ev.evidence_state}
                      </span>
                    </div>
                    <blockquote
                      className="font-body text-gray-700 text-sm leading-relaxed border-l-2 pl-3 mb-2"
                      style={{ borderColor: '#d4d4d4' }}
                    >
                      {ev.passage}
                    </blockquote>
                    <p className="font-body text-xs text-gray-400">
                      <strong className="font-heading font-medium text-gray-500">Provenance:</strong>{' '}
                      {ev.provenance || <span className="italic">Not available</span>}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Sidebar: meta + review action */}
        <div className="space-y-6">
          <div className="border border-gray-200 rounded p-5">
            <h3 className="font-heading font-semibold text-black text-sm mb-4">Finding details</h3>
            <dl className="space-y-3">
              <div>
                <dt className="font-heading text-xs text-gray-400 uppercase tracking-wider">Assigned authority</dt>
                <dd className="font-body text-sm text-black mt-0.5">{finding.assigned_authority}</dd>
              </div>
              <div>
                <dt className="font-heading text-xs text-gray-400 uppercase tracking-wider">Review state</dt>
                <dd
                  className="font-heading text-sm font-semibold mt-0.5"
                  style={{ color: REVIEW_STATE_COLOR[finding.review_state] || '#737373' }}
                >
                  {finding.review_state}
                </dd>
              </div>
              <div>
                <dt className="font-heading text-xs text-gray-400 uppercase tracking-wider">Evidence state</dt>
                <dd className="inline-flex items-center gap-1.5 font-body text-sm text-gray-600 mt-0.5">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: EVIDENCE_DOT[finding.evidence_state] || '#a3a3a3' }}
                  />
                  {EVIDENCE_STATE_PLAIN[finding.evidence_state] || finding.evidence_state}
                </dd>
              </div>
              <div>
                <dt className="font-heading text-xs text-gray-400 uppercase tracking-wider">Finding ID</dt>
                <dd className="font-body text-xs text-gray-400 mt-0.5 break-all">{finding.id}</dd>
              </div>
            </dl>

            {isPending && !result && (
              <button
                onClick={openReview}
                className="w-full mt-5 text-sm font-heading font-semibold text-black rounded transition-colors"
                style={{ backgroundColor: '#03f37c', minHeight: '44px', padding: '10px 16px' }}
                data-testid="open-review-btn"
              >
                Review this finding
              </button>
            )}
            {!isPending && (
              <p className="mt-4 font-body text-xs text-gray-400 text-center">
                This finding has been reviewed.
              </p>
            )}
          </div>

          {/* Canon note */}
          <div className="border border-gray-100 rounded p-4 bg-gray-50">
            <p className="font-heading text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Canon governance
            </p>
            <p className="font-body text-xs text-gray-500 leading-relaxed">
              Approving a recommendation marks it for canon action. A Canon Owner must publish
              the change to active canon. No version is overwritten automatically.
            </p>
          </div>
        </div>
      </div>

      {/* Review Dialog — full-width sheet on mobile */}
      <Dialog open={showReview} onOpenChange={(o) => { if (!submitting) setShowReview(o); }}>
        <DialogContent
          className="max-w-lg w-full"
          data-testid="review-dialog"
          style={{ borderRadius: '4px' }}
        >
          <DialogHeader>
            <DialogTitle className="font-heading font-semibold text-black text-lg">
              Review finding
            </DialogTitle>
            <DialogDescription className="font-body text-sm text-gray-500">
              {finding.title}
            </DialogDescription>
          </DialogHeader>

          {result ? (
            <div className="py-4" data-testid="review-success">
              <p className="font-heading font-semibold text-black mb-1">
                Decision recorded: {result.resulting_state}
              </p>
              {result.canon_note && (
                <p className="font-body text-sm text-gray-600">{result.canon_note}</p>
              )}
              <button
                onClick={() => setShowReview(false)}
                className="mt-4 w-full py-2 text-sm font-heading font-medium bg-gray-100 text-black rounded hover:bg-gray-200 transition-colors"
                data-testid="review-close-btn"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5 mt-2" data-testid="review-form">
              {/* Action selection */}
              <div>
                <label className="font-heading text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-2">
                  Select action
                </label>
                <div className="space-y-2">
                  {ACTIONS.map((a) => (
                    <label
                      key={a.value}
                      className="flex items-center gap-3 p-3 border rounded cursor-pointer transition-colors hover:border-gray-400"
                      style={{ borderColor: action === a.value ? '#000' : '#e5e5e5' }}
                      data-testid={`action-option-${a.value}`}
                    >
                      <input
                        type="radio"
                        name="review-action"
                        value={a.value}
                        checked={action === a.value}
                        onChange={() => { setAction(a.value); setFormError(''); }}
                        className="accent-black"
                        style={{ accentColor: '#03f37c' }}
                      />
                      <span className="font-body text-sm text-black">{a.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="font-heading text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1.5" htmlFor="review-note">
                  Note{NOTE_REQUIRED.includes(action) ? ' (required)' : ' (optional)'}
                </label>
                <textarea
                  id="review-note"
                  value={note}
                  onChange={(e) => { setNote(e.target.value); setFormError(''); }}
                  rows={3}
                  placeholder="Add a note explaining your decision…"
                  className="w-full border border-gray-200 rounded px-3 py-2 font-body text-sm text-black resize-none focus:outline-none focus:border-black"
                  data-testid="review-note-input"
                  style={{ fontFamily: 'Helvetica, Arial, sans-serif' }}
                />
              </div>

              {formError && (
                <p
                  className="font-body text-sm text-black bg-gray-100 px-3 py-2 rounded"
                  data-testid="review-form-error"
                >
                  {formError}
                </p>
              )}

              <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setShowReview(false)}
                  className="flex-1 text-sm font-heading font-medium border border-gray-200 text-gray-600 rounded hover:border-gray-400 transition-colors"
                  style={{ minHeight: '44px', padding: '10px 16px' }}
                  data-testid="review-cancel-btn"
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 text-sm font-heading font-semibold text-black rounded disabled:opacity-50 transition-opacity"
                  style={{ backgroundColor: '#03f37c', minHeight: '44px', padding: '10px 16px' }}
                  data-testid="review-submit-btn"
                >
                  {submitting ? 'Saving…' : `Confirm ${action || 'decision'}`}
                </button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
